// @flow

import React from 'react';
import styled from 'styled-components';
import { Icon, Tooltip, Position } from '@blueprintjs/core';

type Props = {
  typeLabel: string,
  valueLabel: string,
  color: string,
  missing?: boolean,
  size?: 'sm' | 'md',
  onRemove?: () => mixed,
  removeTooltip?: string,
  ariaLabel?: string,
};

const PillRoot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${(p) => (p.size === 'sm' ? '2px 8px' : '4px 10px')};
  border-radius: 999px;
  background: #f5f8fa;
  border: 1px solid #d1d8de;
  font-size: ${(p) => (p.size === 'sm' ? '11px' : '12px')};
  /* line-height: 1 keeps the line-box equal to the content height. With
     1.4 the inline Tooltip wrapper (parent of RemoveButton) had ~5px of
     baseline leading, and the inline-flex RemoveButton aligned to that
     baseline — pushing the SVG above the visual centre of the pill. */
  line-height: 1;
  color: #182026;
  white-space: nowrap;
  max-width: 240px;
`;

const Dot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.color};
  flex: 0 0 auto;
`;

const TypeLabel = styled.span`
  font-weight: 600;
  color: #5c7080;
`;

const ValueLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  ${(p) => (p.missing ? 'color: #a7b6c2; font-style: italic;' : '')}
`;

const RemoveButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: #5c7080;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Without this, the pill's line-height: 1.4 leaks into the button's line
     box and the SVG icon (which sits on the text baseline) renders above
     the visual centre. Forcing line-height to 0 removes that leading. */
  line-height: 0;
  &:hover {
    color: #182026;
  }
  &:focus {
    outline: 1px solid #137cbd;
    outline-offset: 1px;
  }
`;

const CategorizedPill = ({
  typeLabel,
  valueLabel,
  color,
  missing,
  size = 'md',
  onRemove,
  removeTooltip,
  ariaLabel,
}: Props) => {
  const accessibleName = ariaLabel || `${typeLabel}: ${valueLabel}`;
  return (
    <PillRoot size={size} aria-label={accessibleName}>
      <Dot color={color} aria-hidden />
      <TypeLabel>{typeLabel}:</TypeLabel>
      <ValueLabel missing={missing}>{valueLabel}</ValueLabel>
      {onRemove && (
        <Tooltip
          content={removeTooltip || `Remove ${typeLabel}: ${valueLabel}`}
          position={Position.TOP}
        >
          <RemoveButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            aria-label={`Remove ${typeLabel}: ${valueLabel}`}
          >
            <Icon icon="cross" iconSize={size === 'sm' ? 10 : 12} />
          </RemoveButton>
        </Tooltip>
      )}
    </PillRoot>
  );
};

export default CategorizedPill;
